/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component, MouseEvent, FormEvent } from 'react';
import { AppState } from '../../app.state';
import { User } from '../../_shared/interfaces/user';
import { message } from 'antd';
import { withRouter, RouteComponentProps } from 'react-router';
import FormTitle from '../../_shared/form-title';
import { Button } from '../../_shared/button';
import styles from './change-password.styles';
import { buildErrorMsgFromForm, stopProp } from '../../utils';
import { changePassword } from '../../_shared/services/authentication.service';
import { WithForm } from '../../_shared/form';

const MATCH_ERROR_COLOR = '#ff5630';

interface IProps extends RouteComponentProps {
  sState: AppState;
  updateUser: (user: User) => Promise<User>;
  form?: any;
}

export class _ChangePasswordComponent extends Component<IProps> {
  state = {
    loading: false,
    // AER-1655: when New + Confirm are both populated and differ (on blur),
    // surface an in-context error below Confirm. Lower priority than the
    // length rule, which AntD shows on the field itself.
    pwMismatch: false,
  };
  // AER-1655: validate New/Confirm match when either loses focus and both are
  // populated. Only flag a mismatch once the length rule is satisfied so the
  // "at least 10 characters" error keeps priority.
  checkMatch = () => {
    const { getFieldValue } = this.props.form;
    const pw1 = getFieldValue('pw1') || '';
    const pw2 = getFieldValue('pw2') || '';

    const lengthOk = pw1.length >= 10 && pw2.length >= 10;
    const pwMismatch = !!pw1 && !!pw2 && lengthOk && pw1 !== pw2;
    if (pwMismatch !== this.state.pwMismatch) {
      this.setState({ pwMismatch });
    }
  };
  toDisplay = () => [
    {
      title: 'Current Password',
      var: 'currentPassword',
      type: 'password',
    },
    {
      title: 'New Password',
      var: 'pw1',
      type: 'password',
      options: {
        validateTrigger: 'onBlur',
        rules: [
          {
            max: 60,
            message: 'Password is limited to 60 characters. ',
          },
          {
            min: 10,
            message: 'Passwords must be at least 10 characters. ',
          },
        ],
      },
      props: {
        onBlur: this.checkMatch,
      },
    },
    {
      title: 'Confirm New Password',
      var: 'pw2',
      type: 'password',
      options: {
        validateTrigger: 'onBlur',
        rules: [
          {
            max: 60,
            message: 'Password is limited to 60 characters. ',
          },
          {
            min: 10,
            message: 'Passwords must be at least 10 characters. ',
          },
        ],
      },
      props: {
        onBlur: this.checkMatch,
      },
      subtext: () =>
        this.state.pwMismatch ? (
          <div css={css(`color: ${MATCH_ERROR_COLOR}; font-size: 14px;`)}>
            Passwords must match.
          </div>
        ) : null,
    },
  ];
  submit = async (e: MouseEvent | FormEvent) => {
    stopProp(e);
    if (this.state.loading) {
      return;
    }

    this.state.loading = true;
    this.setState(this.state);

    const { form } = this.props;

    form.validateFieldsAndScroll(async (err: any, values: any) => {
      if (err) {
        const error = buildErrorMsgFromForm(err);
        message.error(error);
        return this.setState({ loading: false });
      }

      try {
        const { pw1, pw2, currentPassword } = values;

        if (pw1 !== pw2) {
          this.setState({ pwMismatch: true, loading: false });
          return;
        }

        await changePassword(currentPassword, pw1);

        message.success('Password updated successfully!');
        // AER-1655: clear Current/New/Confirm after a successful save so the
        // signed-in Change Password form returns to its empty state (QA: the
        // fields retained their values post-save). This form seeds no
        // initialValues, so resetFields() empties all three; the setState()
        // re-render below then recomputes btnDisabled from the now-empty fields,
        // re-disabling Save.
        form.resetFields();
        this.setState({
          loading: false,
          isEdit: false,
          pwMismatch: false,
        });
      } catch (err) {
        const msg = err.message.toLowerCase();
        message.error(
          msg.indexOf('incorrect username') > -1
            ? 'Current Password entered is incorrect.'
            : 'Change Password Failed: ' + err.message
        );
        return this.setState({
          loading: false,
        });
      }
    });
  };
  render() {
    const {
      sState: {
        auth: { user },
      },
    } = this.props;
    const { getFieldValue, getFieldsError, Form } = this.props.form;
    const { loading, pwMismatch } = this.state;

    // AER-1655: disable Save whenever any validation error is currently
    // present (field-level rule errors or the New/Confirm mismatch), or when
    // a required field is still empty.
    const hasFieldErrors = (getFieldsError ? getFieldsError() : []).some(
      (f: { errors: string[] }) => f.errors && f.errors.length > 0
    );
    const btnDisabled =
      !(
        getFieldValue('currentPassword') &&
        getFieldValue('pw1') &&
        getFieldValue('pw2')
      ) ||
      pwMismatch ||
      hasFieldErrors;

    return user ? (
      <div css={css(styles.container)}>
        <FormTitle title={`Change Password`} />


        <Form
          layout="vertical"
          forceUpdate={this}
          onSubmit={this.submit}
          items={this.toDisplay()}
        >
          <Button
            disabled={btnDisabled}
            title={'Save'}
            loading={loading}
            onClick={this.submit}
          />
        </Form>
      </div>
    ) : (
      <div>No User Provided</div>
    );
  }
}

export const ChangePasswordComponent = WithForm(withRouter(_ChangePasswordComponent));
